import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageTransition } from '../transitions/PageTransition';
import { useCursorContext } from '../../hooks/useCursorContext';

export function LievyPrivacyPage() {
  const { setCursorState } = useCursorContext();

  return (
    <PageTransition>
      <div className="min-h-screen bg-[#0a0a0a] pt-32 pb-20">
        <div className="fixed top-24 left-8 md:left-12 z-40">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/40 hover:text-emerald-500 transition-colors group"
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-xs tracking-wider uppercase">Zurück</span>
          </Link>
        </div>

        <div className="max-w-3xl mx-auto px-8 md:px-12">
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4">
            Datenschutzerklärung
          </h1>
          <p className="text-white/40 mb-12">Lievy — Date Night App</p>

          <div className="space-y-10 text-white/70">
            <section>
              <h2 className="text-lg text-white mb-4">1. Verantwortlicher</h2>
              <p className="leading-relaxed">
                Niko Knez / NK Design<br />
                Köln-Aachener-Straße 155<br />
                50127 Bergheim<br />
                E-Mail: niko@design-nk.de
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">2. Welche Daten wir erheben</h2>
              <p className="leading-relaxed mb-4">
                Wenn du Lievy nutzt, erheben wir folgende Daten:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Account-Daten:</strong> E-Mail-Adresse, Display-Name (bei Registrierung oder Apple Sign In)</li>
                <li><strong className="text-white">Profil-Daten:</strong> Beziehungsstatus, Date-Präferenzen, Date-Häufigkeit</li>
                <li><strong className="text-white">Nutzungsdaten:</strong> Swipes, Matches, geplante Dates, Streaks</li>
                <li><strong className="text-white">Standort:</strong> Nur wenn du es erlaubst, um Venues in der Nähe zu finden</li>
                <li><strong className="text-white">Fotos:</strong> Nur wenn du aktiv Fotos zu Dates oder deinem Profil hochlädst</li>
                <li><strong className="text-white">Push-Token:</strong> Für Benachrichtigungen, wenn du sie aktivierst</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">3. Warum wir Daten erheben</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Um dir und deinem Partner passende Date-Ideen vorzuschlagen</li>
                <li>Um euch als Paar zu verbinden (Pairing-Code)</li>
                <li>Um Matches zu erkennen wenn beide die gleiche Idee mögen</li>
                <li>Um euch an geplante Dates zu erinnern</li>
                <li>Um euren Streak und Erfolge zu tracken</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">4. Hosting & Drittanbieter</h2>
              <p className="leading-relaxed mb-4">
                Lievy nutzt <strong className="text-white">Supabase</strong> (Supabase Inc., San Francisco, USA)
                als Backend-Infrastruktur. Deine Daten werden auf Supabase-Servern gespeichert und verarbeitet.
                Supabase ist DSGVO-konform und bietet Standard-Vertragsklauseln für den Datentransfer in die USA.
              </p>
              <p className="leading-relaxed">
                Für Bilder nutzen wir die <strong className="text-white">Pexels API</strong> (lizenzfreie Fotos).
                Dabei werden keine persönlichen Daten an Pexels übermittelt.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">5. Apple Sign In</h2>
              <p className="leading-relaxed">
                Wenn du dich mit Apple anmeldest, erhalten wir je nach deiner Wahl deine E-Mail-Adresse
                (oder eine von Apple generierte Relay-Adresse) und deinen Namen. Apple verarbeitet die
                Authentifizierung — wir erhalten nur die Daten die du freigibst.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">6. In-App-Käufe</h2>
              <p className="leading-relaxed">
                Lievy Plus Abonnements werden über Apple verwaltet. Wir haben keinen Zugriff auf deine
                Zahlungsdaten. Apple verarbeitet alle Transaktionen. Wir erhalten nur die Information
                ob ein aktives Abo besteht.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">7. Push-Benachrichtigungen</h2>
              <p className="leading-relaxed">
                Wenn du Push-Benachrichtigungen aktivierst, speichern wir deinen Device-Token um dir
                Benachrichtigungen zu senden (z.B. Streak-Erinnerungen, Matches, Date-Erinnerungen).
                Du kannst Benachrichtigungen jederzeit in den iOS-Einstellungen oder in der App deaktivieren.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">8. Deine Rechte</h2>
              <p className="leading-relaxed mb-4">
                Du hast jederzeit das Recht auf:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Auskunft:</strong> Welche Daten wir über dich gespeichert haben</li>
                <li><strong className="text-white">Löschung:</strong> Löschung deines Accounts und aller Daten</li>
                <li><strong className="text-white">Berichtigung:</strong> Korrektur falscher Daten</li>
                <li><strong className="text-white">Datenübertragbarkeit:</strong> Export deiner Daten</li>
                <li><strong className="text-white">Widerspruch:</strong> Gegen die Verarbeitung deiner Daten</li>
              </ul>
              <p className="leading-relaxed mt-4">
                Schreib uns einfach an <strong className="text-white">niko@design-nk.de</strong> —
                wir antworten innerhalb von 30 Tagen.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">9. Datenlöschung</h2>
              <p className="leading-relaxed">
                Du kannst deinen Account jederzeit in der App unter Profil → Einstellungen → "Account löschen"
                vollständig löschen. Dabei werden alle deine Daten unwiderruflich entfernt.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">10. Kein Tracking</h2>
              <p className="leading-relaxed">
                Lievy verwendet <strong className="text-white">kein Tracking</strong>, keine Analyse-Tools
                von Drittanbietern und keine Werbung. Wir verkaufen deine Daten nicht. Punkt.
              </p>
            </section>

            <section>
              <p className="text-white/40 text-sm">
                Stand: März 2026
              </p>
            </section>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
